import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'
import type {LinkedPullRequest$key} from './__generated__/LinkedPullRequest.graphql'
import {ActionList, ActionMenu, Box, Text} from '@primer/react'
import type {LinkedPullRequests$data, LinkedPullRequests$key} from './__generated__/LinkedPullRequests.graphql'
import {memo} from 'react'
import {LABELS} from '../..'
import {LinkedPullRequest} from './LinkedPullRequest'

const LinkedPullRequestsPulls = graphql`
  fragment LinkedPullRequests on Issue {
    repository {
      nameWithOwner
    }
    linkedPullRequests: closedByPullRequestsReferences(first: 10, includeClosedPrs: false, orderByState: true) {
      edges {
        node {
          repository {
            nameWithOwner
          }
          state
          ...LinkedPullRequest
        }
      }
    }
  }
`

export const LinkedPullRequests = memo(function LinkedPullRequests({
  pullRequestsData,
  isSticky,
}: {
  pullRequestsData: LinkedPullRequests$key
  isSticky?: boolean
}) {
  const data = useFragment(LinkedPullRequestsPulls, pullRequestsData)
  const {mergedPullRequests, openPullRequests, externalPrsCount, issueRepositoryName, pullRequests} =
    getPullRequests(data)

  const showCondensedState =
    pullRequests.length > 3 || externalPrsCount > 1 || (openPullRequests.length > 0 && mergedPullRequests.length > 0)

  return (
    <>
      {showCondensedState && isSticky && (
        <Text sx={{color: 'fg.muted', fontSize: '12px', pl: 1}}>
          {LABELS.linkedPullRequests(mergedPullRequests.length, openPullRequests.length)}
        </Text>
      )}
      {showCondensedState && !isSticky && (
        <ActionMenu>
          <ActionMenu.Button size="small" variant="invisible" sx={{color: 'fg.default'}}>
            {LABELS.linkedPullRequests(mergedPullRequests.length, openPullRequests.length)}
          </ActionMenu.Button>
          <ActionMenu.Overlay sx={{display: isSticky ? 'none' : 'block'}}>
            <ActionList>
              {mergedPullRequests.length > 0 && (
                <ListRenderer
                  list={mergedPullRequests}
                  text={LABELS.mergedPrs}
                  issueRepositoryName={issueRepositoryName}
                />
              )}
              {openPullRequests?.length > 0 && mergedPullRequests.length > 0 ? <ActionList.Divider /> : null}
              {openPullRequests.length > 0 ? (
                <ListRenderer list={openPullRequests} text={LABELS.openPrs} issueRepositoryName={issueRepositoryName} />
              ) : null}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {!showCondensedState && pullRequests.length > 0 && (
        <Box sx={{display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', overflow: 'hidden', pl: 1}}>
          <Text sx={{fontSize: 0}}>{mergedPullRequests.length > 0 ? LABELS.mergedPrs : LABELS.openPrs}&nbsp;</Text>
          {pullRequests.map((pull, i) => (
            <Box key={i} sx={{display: 'flex'}}>
              <LinkedPullRequest pullKey={pull} issueRepositoryName={issueRepositoryName} isSticky={isSticky} />
              {i < pullRequests.length - 1 && <Text>,&nbsp;</Text>}
            </Box>
          ))}
        </Box>
      )}
    </>
  )
})

const ListRenderer = ({
  list,
  text,
  issueRepositoryName,
}: {
  list: LinkedPullRequest$key[]
  text: string
  issueRepositoryName: string
}) => {
  return (
    <>
      <ActionList.GroupHeading aria-label={text}>{text}</ActionList.GroupHeading>
      {list.map((pull, i) => (
        <LinkedPullRequest pullKey={pull} issueRepositoryName={issueRepositoryName} isListItem={true} key={i} />
      ))}
    </>
  )
}

const getPullRequests = (data: LinkedPullRequests$data) => {
  const mergedPullRequests: LinkedPullRequest$key[] = []
  const openPullRequests: LinkedPullRequest$key[] = []
  const pullRequests: LinkedPullRequest$key[] = []
  const issueRepositoryName = data?.repository?.nameWithOwner.toLocaleLowerCase()

  let externalPrsCount = 0

  data?.linkedPullRequests?.edges?.flatMap(a => {
    if (!a || !a.node) return
    const isExternal = a.node.repository.nameWithOwner.toLocaleLowerCase() !== issueRepositoryName.toLocaleLowerCase()
    isExternal && externalPrsCount++
    if (a.node.state === 'MERGED') {
      isExternal ? mergedPullRequests.push({...a.node}) : mergedPullRequests.unshift({...a.node})
    } else {
      isExternal ? openPullRequests.push({...a.node}) : openPullRequests.unshift({...a.node})
    }
    isExternal ? pullRequests.push({...a.node}) : pullRequests.unshift({...a.node})
  })

  return {mergedPullRequests, openPullRequests, externalPrsCount, issueRepositoryName, pullRequests}
}

try{ LinkedPullRequests.displayName ||= 'LinkedPullRequests' } catch {}
try{ ListRenderer.displayName ||= 'ListRenderer' } catch {}